import { useState, useEffect } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { format } from "date-fns";
import {
  User, Building2, MapPin, GraduationCap,
  Briefcase, ChevronLeft, Edit, Shield,
} from "lucide-react";
import { toast } from "react-toastify";
import PageLoader from "../../helpers/PageLoader";
import userImage from "../../assets/images/userImage.png";
import api from "../../common/apiClient";
import SummaryApi from "../../common/SummaryApi";
import { GiReceiveMoney } from "react-icons/gi";
import { useSelector } from "react-redux";
import { useCallback } from "react";
const TABS = [
  { id: "personal", label: "Personal", icon: User },
  { id: "employment", label: "Employment", icon: Building2 },
  { id: "finance", label: "Finance", icon: GiReceiveMoney }, 
  { id: "education", label: "Education", icon: GraduationCap },
  { id: "experience", label: "Experience", icon: Briefcase },
  { id: "contact", label: "Contact & Address", icon: MapPin },
];

const fmtDate = (d) => (d ? format(new Date(d), "dd MMM yyyy") : "—");

const InfoCard = ({ label, value }) => (
  <div className="bg-gray-50 rounded-lg p-3">
    <p className="text-xs text-gray-500 mb-0.5">{label}</p>
    <p className="text-sm font-medium text-gray-800 break-words">{value || "—"}</p>
  </div>
);

const ViewOneEmployee = () => {
  const { portalName, employeeId } = useParams();
  const navigate = useNavigate();
  const [employee, setEmployee] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("personal");
  // const [deleting, setDeleting] = useState(false);
  const [financeData, setFinanceData] = useState(null);
  const [loadingFinance, setLoadingFinance] = useState(false);
  const { user } = useSelector(state => state.user); 
  
  // செக்யூரிட்டி: அட்மின் அல்லது பேரோல் அனுமதி உள்ளதா?
  const canViewFinance = user?.role === 'admin' || user?.role === 'payroll' || user?.role === 'owner';

  useEffect(() => {
    const load = async () => {
      try {
        const res = await api.get(
          SummaryApi.getOneEmployee?.url(employeeId) || `/api/employees/${employeeId}`
        );
        setEmployee(res.data.data);
      } catch {
        toast.error("Failed to load employee");
        navigate(`/${portalName}/hr/employee-management`);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [employeeId]); // eslint-disable-line react-hooks/exhaustive-deps


  // 1. fetchFinance-ஐ useCallback-க்குள் மாற்றவும்
  const fetchFinance = useCallback(async () => {
    setLoadingFinance(true);
    try {
      const res = await api.get(SummaryApi.getOneEmployeeFinance?.url(employeeId));
      setFinanceData(res.data.data);
    } catch {
      toast.error("Failed to load finance details");
    } finally {
      setLoadingFinance(false);
    }
  }, [employeeId]); // employeeId மாறினால் மட்டும் இந்த ஃபங்ஷன் மாறும்

  // 2. useEffect-ல் விடுபட்ட dependencies-ஐச் சேர்க்கவும்
  useEffect(() => {
    if (activeTab === "finance" && !financeData && canViewFinance) {
      fetchFinance();
    }
  }, [activeTab, financeData, canViewFinance, fetchFinance]); 



  // const handleDelete = async () => {
  //   if (!window.confirm(`Delete ${employee?.firstName} ${employee?.lastName}? This cannot be undone.`)) return;
  //   setDeleting(true);
  //   try {
  //     await api.delete(SummaryApi.deleteEmployee?.url(employeeId) || `/api/employees/${employeeId}`);
  //     toast.success("Employee deleted");
  //     navigate(`/${portalName}/hr/employee-management`);
  //   } catch (err) {
  //     toast.error(err.response?.data?.message || "Delete failed");
  //   } finally {
  //     setDeleting(false);
  //   }
  // };

  if (loading) return <PageLoader />;
  if (!employee) return null;

  const e = employee;
  const statusColors = {
    active: "bg-green-100 text-green-700",
    on_leave: "bg-yellow-100 text-yellow-700",
    suspended: "bg-orange-100 text-orange-700",
    resigned: "bg-gray-100 text-gray-600",
    terminated: "bg-red-100 text-red-700",
  };

  return (
    <div className="p-2">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(`/${portalName}/hr/employee-management`)} className="p-2 hover:bg-gray-100 rounded-full">
            <ChevronLeft className="w-5 h-5" />
          </button>
          <h1 className="text-xl font-bold text-gray-900">Employee Details</h1>
        </div>
        <div className="flex gap-2">
          <Link
            to={`/${portalName}/hr/employee-management/employee-finance/${employeeId}`}
            className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
          >
            <GiReceiveMoney  className="w-4 h-4" /> Finance
          </Link>
          <Link
            to={`/${portalName}/hr/employee-management/employee/edit/${employeeId}`}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
          >
            <Edit className="w-4 h-4" /> Edit
          </Link>
          {/* <button
            onClick={handleDelete}
            disabled={deleting || e.role === "owner"}
            className="flex items-center gap-2 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
          >
            <Trash2 className="w-4 h-4" /> {deleting ? "Deleting..." : "Delete"}
          </button> */}
        </div>
      </div>

      {/* Profile card */}
      <div className="bg-white rounded-xl p-5 mb-5 flex flex-col sm:flex-row items-center sm:items-start gap-4">
        <img
          src={e.profilePicture && e.profilePicture !== "null" ? e.profilePicture : userImage}
          alt={`${e.firstName} ${e.lastName}`}
          className="w-24 h-24 rounded-full object-cover border-4 border-red-100 flex-shrink-0"
        />
        <div className="text-center sm:text-left flex-1">
          <h2 className="text-2xl font-bold text-gray-900">{e.firstName} {e.lastName}</h2>
          <p className="text-gray-500 text-sm mt-0.5">{e.designationId?.title || "—"} · {e.departmentId?.name || "—"}</p>
          <p className="text-gray-500 text-xs mt-0.5">{e.userId}</p>
          <div className="flex flex-wrap gap-2 justify-center sm:justify-start mt-2">
            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${statusColors[e.employmentStatus] || "bg-gray-100 text-gray-600"}`}>
              {e.employmentStatus?.replace("_", " ") || "—"}
            </span>
            <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 font-medium">
              {e.employmentType?.replace("_", " ") || "—"}
            </span>
            {e.role === "owner" && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-purple-100 text-purple-700 font-medium flex items-center gap-1">
                <Shield className="w-3 h-3" /> Owner
              </span>
            )}
            {!e.isActive && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-600 font-medium">Inactive</span>
            )}
          </div>
        </div>
        <div className="text-center sm:text-right text-xs text-gray-400 space-y-1">
          {e.customRoleId?.roleName && <p>Role: <span className="text-gray-600 font-medium">{e.customRoleId.roleName}</span></p>}
          {e.workMode && <p>Mode: <span className="text-gray-600 font-medium capitalize">{e.workMode}</span></p>}
          {e.workLocation && <p>Location: <span className="text-gray-600 font-medium">{e.workLocation}</span></p>}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex overflow-x-auto gap-1 bg-gray-100 p-1 rounded-lg mb-5">
        {TABS.map((tab) => {
          if (tab.id === "finance" && !canViewFinance) return null;
          
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-md text-sm font-medium whitespace-nowrap transition-all ${
                activeTab === tab.id ? "bg-white text-red-600 shadow-sm" : "text-gray-600 hover:text-gray-900"
              }`}
            >
              <tab.icon className="w-3.5 h-3.5" /> {tab.label}
            </button>
          );
        })}
      </div>

      {/* Tab content */}
      <div className="bg-white rounded-xl p-5">
        {/* ─── Personal ─── */}
        {activeTab === "personal" && (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <InfoCard label="Email" value={e.email} />
            <InfoCard label="Mobile" value={e.mobile} />
            <InfoCard label="Gender" value={e.gender?.replace("_", " ")} />
            <InfoCard label="Date of Birth" value={fmtDate(e.dob)} />
            <InfoCard label="Blood Group" value={e.bloodGroup} />
            <InfoCard label="Marital Status" value={e.maritalStatus} />
            <InfoCard label="Auth Provider" value={e.authProvider} />
            <InfoCard label="Verified" value={e.isVerified ? "Yes" : "No"} />
            <InfoCard label="Created On" value={fmtDate(e.createdAt)} />
            {e.createdBy && <InfoCard label="Created By" value={`${e.createdBy.firstName} ${e.createdBy.lastName}`} />}
            {e.updatedBy && <InfoCard label="Last Updated By" value={`${e.updatedBy.firstName} ${e.updatedBy.lastName}`} />}
          </div>
        )}

        {/* ─── Employment ─── */}
        {activeTab === "employment" && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <InfoCard label="Division" value={e.divisionId?.name} />
              <InfoCard label="Department" value={e.departmentId?.name} />
              <InfoCard label="Designation" value={e.designationId?.title} />
              <InfoCard label="Employment Type" value={e.employmentType?.replace("_", " ")} />
              <InfoCard label="Employment Status" value={e.employmentStatus?.replace("_", " ")} />
              <InfoCard label="Work Mode" value={e.workMode} />
              <InfoCard label="Work Location" value={e.workLocation} />
              <InfoCard label="Date of Joining" value={fmtDate(e.doj)} />
              <InfoCard label="Probation End" value={fmtDate(e.probationEndDate)} />
              <InfoCard label="Confirmation Date" value={fmtDate(e.confirmationDate)} />
              <InfoCard label="Payroll Eligible" value={e.payrollEligible ? "Yes" : "No"} />
              <InfoCard label="Attendance Device ID" value={e.attendanceDeviceId} />
            </div>

            {e.reportingManagerId && (
              <div>
                <p className="text-xs text-gray-500 mb-2 font-medium uppercase tracking-wide">Reporting Manager</p>
                <div className="flex items-center gap-3 bg-gray-50 rounded-lg p-3">
                  <img
                    src={e.reportingManagerId.profilePicture && e.reportingManagerId.profilePicture !== "null" ? e.reportingManagerId.profilePicture : userImage}
                    alt="manager"
                    className="w-10 h-10 rounded-full object-cover"
                  />
                  <div>
                    <p className="text-sm font-medium">{e.reportingManagerId.firstName} {e.reportingManagerId.lastName}</p>
                    <p className="text-xs text-gray-500">{e.reportingManagerId.userId}</p>
                  </div>
                </div>
              </div>
            )}

            {e.secondaryReportingManagerIds?.length > 0 && (
              <div>
                <p className="text-xs text-gray-500 mb-2 font-medium uppercase tracking-wide">Secondary Managers</p>
                <div className="flex flex-wrap gap-2">
                  {e.secondaryReportingManagerIds.map((m) => (
                    <div key={m._id} className="flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-2">
                      <img src={m.profilePicture && m.profilePicture !== "null" ? m.profilePicture : userImage} alt="mgr" className="w-6 h-6 rounded-full object-cover" />
                      <span className="text-sm">{m.firstName} {m.lastName}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}


        {/* ─── Finance Tab ─── */}
        {activeTab === "finance" && canViewFinance && (
          <div>
            {loadingFinance ? (
              <div className="flex justify-center py-10"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-500"></div></div>
            ) : !financeData ? (
              <p className="text-gray-400 text-sm text-center py-8">Finance record not found.</p>
            ) : (
              <div className="space-y-6">
                {/* Govt IDs */}
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                   <InfoCard label="PAN Number" value={financeData.panNumber} />
                   <InfoCard label="Aadhaar Number" value={financeData.aadhaarNumber} />
                   <InfoCard label="UAN Number" value={financeData.uanNumber} />
                   <InfoCard label="ESI Number" value={financeData.esiNumber} />
                   <InfoCard label="PF Account Number" value={financeData.pfAccountNumber} />
                   <InfoCard label="Pay Frequency" value={financeData.payFrequency} />
                </div>
                
                {/* Bank Details */}
                <div className="border-t pt-4">
                   <p className="text-xs text-gray-500 mb-3 font-medium uppercase tracking-wide">Bank Details</p>
                   <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      <InfoCard label="Account Holder" value={financeData.bankDetails?.accountHolderName} />
                      <InfoCard label="Bank Name" value={financeData.bankDetails?.bankName} />
                      <InfoCard label="Account Number" value={financeData.bankDetails?.accountNumber} />
                      <InfoCard label="IFSC Code" value={financeData.bankDetails?.ifscCode} />
                      <InfoCard label="Branch" value={financeData.bankDetails?.branchName} />
                   </div>
                </div>

                {/* Nominees */}
                <div className="border-t pt-4">
                   <p className="text-xs text-gray-500 mb-3 font-medium uppercase tracking-wide">Nominees</p>
                   <div className="space-y-2">
                     {financeData.nominees?.map((nom, idx) => (
                       <div key={idx} className="bg-gray-50 p-3 rounded-lg grid grid-cols-3 gap-2">
                         <InfoCard label="Name" value={nom.name} />
                         <InfoCard label="Relationship" value={nom.relationship} />
                         <InfoCard label="Share %" value={`${nom.sharePercent}%`} />
                       </div>
                     ))}
                   </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ─── Education ─── */}
        {activeTab === "education" && (
          <div>
            {(!e.education || e.education.length === 0) ? (
              <p className="text-gray-400 text-sm text-center py-8">No education details added.</p>
            ) : (
              <div className="space-y-4">
                {e.education.map((ed, i) => (
                  <div key={i} className="border-l-2 border-red-300 pl-4 py-1">
                    <p className="font-semibold text-gray-800">{ed.degree}</p>
                    <p className="text-sm text-gray-600">{ed.institution}</p>
                    {ed.fieldOfStudy && <p className="text-xs text-gray-500 mt-0.5">{ed.fieldOfStudy}</p>}
                    <div className="flex gap-3 mt-1 text-xs text-gray-400">
                      {(ed.startYear || ed.endYear) && <span>{ed.startYear} – {ed.endYear || "present"}</span>}
                      {ed.grade && <span>· {ed.grade}</span>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ─── Work Experience ─── */}
        {activeTab === "experience" && (
          <div>
            {(!e.workExperience || e.workExperience.length === 0) ? (
              <p className="text-gray-400 text-sm text-center py-8">No previous work experience added.</p>
            ) : (
              <div className="space-y-4">
                {e.workExperience.map((ex, i) => (
                  <div key={i} className="border-l-2 border-blue-300 pl-4 py-1">
                    <p className="font-semibold text-gray-800">{ex.jobTitle}</p>
                    <p className="text-sm text-gray-600">{ex.companyName}</p>
                    <p className="text-xs text-gray-400 mt-0.5">
                      {ex.startDate ? fmtDate(ex.startDate) : "—"} → {ex.isCurrent ? "Present" : ex.endDate ? fmtDate(ex.endDate) : "—"}
                    </p>
                    {ex.description && <p className="text-xs text-gray-500 mt-1">{ex.description}</p>}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ─── Contact & Address ─── */}
        {activeTab === "contact" && (
          <div className="space-y-5">
            <div>
              <p className="text-xs text-gray-500 mb-3 font-medium uppercase tracking-wide">Address</p>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                <InfoCard label="Street" value={e.address?.street} />
                <InfoCard label="City" value={e.address?.city} />
                <InfoCard label="State" value={e.address?.state} />
                <InfoCard label="Country" value={e.address?.country} />
                <InfoCard label="Postal Code" value={e.address?.postalCode} />
              </div>
            </div>
            {(e.emergencyContact?.name || e.emergencyContact?.mobile) && (
              <div>
                <p className="text-xs text-gray-500 mb-3 font-medium uppercase tracking-wide">Emergency Contact</p>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  <InfoCard label="Name" value={e.emergencyContact.name} />
                  <InfoCard label="Relationship" value={e.emergencyContact.relationship} />
                  <InfoCard label="Mobile" value={e.emergencyContact.mobile} />
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ViewOneEmployee;